import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/store/authStore'
import { useApi } from '@/hooks/useApi'
import LanguageSwitcher from '@/components/LanguageSwitcher'

interface QuotaStatus {
  role: string
  daily_limit: number
  daily_used: number
  daily_remaining: number
  monthly_limit: number
  monthly_used: number
  monthly_remaining: number
}

export default function Navbar() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { firebaseUser, logout, isLoading, error, clearError } = useAuthStore()
  const { api } = useApi()
  const [showMenu, setShowMenu] = useState(false)
  const [quota, setQuota] = useState<QuotaStatus | null>(null)
  const [quotaLoading, setQuotaLoading] = useState(false)
  
  const handleLogout = async () => {
    try {
      await logout()
      navigate('/login')
    } catch {
      // Error handled by store
    }
  }
  
  const displayName = firebaseUser?.displayName || firebaseUser?.email?.split('@')[0] || 'User'
  const initials = (firebaseUser?.displayName || firebaseUser?.email || 'U')[0].toUpperCase()
  
  // Fetch quota status
  useEffect(() => {
    if (!firebaseUser) return
    
    const fetchQuota = async () => {
      setQuotaLoading(true)
      try {
        const res = await api.get<QuotaStatus>('/auth/quota/status')
        setQuota(res.data)
      } catch {
        // Silently fail — quota is optional
      } finally {
        setQuotaLoading(false)
      }
    }
    
    fetchQuota()
  }, [firebaseUser])
  
  const isAdmin = firebaseUser && quota?.role === 'admin'
  
  return (
    <>
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          {/* Logo */}
          <button onClick={() => navigate('/')} className="flex items-center gap-2">
            <span className="text-xl font-bold text-blue-600">{t('app.name')}</span>
          </button>
          
          {/* Nav Links */}
          <nav className="hidden md:flex items-center gap-6">
            <button onClick={() => navigate('/')} className="text-gray-600 hover:text-gray-900 text-sm font-medium">
              {t('nav.home')}
            </button>
            {firebaseUser && (
              <button onClick={() => navigate('/search')} className="text-gray-600 hover:text-gray-900 text-sm font-medium">
                {t('nav.search')}
              </button>
            )}
            {isAdmin && (
              <button onClick={() => navigate('/admin')} className="text-gray-600 hover:text-gray-900 text-sm font-medium">
                {t('nav.admin')}
              </button>
            )}
          </nav>
          
          {/* Language Switcher + User Menu */}
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <div className="relative">
              {firebaseUser ? (
                <>
                  <button
                    onClick={() => setShowMenu(!showMenu)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 font-medium text-sm">{initials}</span>
                    </div>
                    <span className="text-sm font-medium text-gray-700 hidden sm:block">{displayName}</span>
                  </button>
                  
                  {/* Dropdown */}
                  {showMenu && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                      <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                        {/* Quota Info */}
                        {quotaLoading ? (
                          <div className="p-4 text-center text-sm text-gray-400">{t('nav.loadingQuota')}</div>
                        ) : (
                          <div className="p-4 border-b border-gray-100">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-medium text-gray-500 uppercase">{t('nav.dailyQuota')}</span>
                              <span className="text-xs text-blue-600 font-medium">{quota?.daily_used}/{quota?.daily_limit}</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full ${
                                  (quota?.daily_remaining ?? 0) <= 0 ? 'bg-red-500' :
                                  (quota?.daily_remaining ?? 0) <= 2 ? 'bg-yellow-500' : 'bg-blue-500'
                                }`}
                                style={{ width: `${quota ? (quota.daily_used / quota.daily_limit) * 100 : 0}%` }}
                              ></div>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              {quota?.daily_remaining ?? 0} {t('nav.queriesRemaining')}
                            </p>
                            
                            <div className="flex items-center justify-between mt-3">
                              <span className="text-xs font-medium text-gray-500 uppercase">{t('nav.monthlyQuota')}</span>
                              <span className="text-xs text-blue-600 font-medium">{quota?.monthly_used}/{quota?.monthly_limit}</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                              <div
                                className="h-2 rounded-full bg-purple-500"
                                style={{ width: `${quota ? (quota.monthly_used / quota.monthly_limit) * 100 : 0}%` }}
                              ></div>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              {quota?.monthly_remaining ?? 0} {t('nav.queriesRemaining')}
                            </p>
                          </div>
                        )}
                        
                        {/* Menu Items */}
                        <div className="p-2">
                          <button onClick={handleLogout} className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                            {t('nav.signOut')}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <button
                  onClick={() => navigate('/login')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
                >
                  {t('nav.signIn')}
                </button>
              )}
            </div>
          </div>
        </div>
      </header>
      
      {/* Error Toast */}
      {error && (
        <div className="fixed bottom-4 right-4 max-w-md bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg z-50">
          <div className="flex items-start justify-between">
            <p className="text-sm text-red-700">{error}</p>
            <button onClick={clearError} className="text-red-500 hover:text-red-700 ml-4">✕</button>
          </div>
        </div>
      )}
      
      {/* Loading Spinner */}
      {isLoading && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 shadow-lg">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
      )}
    </>
  )
}
